import React, {
  useEffect,
  useState,
  useMemo,
  useRef,
  useCallback,
} from "react";
import { Box, Grid, IconButton, Tooltip } from "@mui/material";
import FlexBetween from "../../components/FlexBetween";
import DataTable from "../../components/DataGrids/DataGrid";
import { GridColDef } from "@mui/x-data-grid";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import useFetchPaginatedComparisonResearchQueries from "../../hooks/useFetchPaginatedComparisonResearchQueries";
import {
  getFormattedDateTime,
  downloadComparisonResearchQueryExportFile,
  useToast,
} from "../../services/utils";
import { useRowsPerPage } from "../../hooks/useRowsPerPage";
import {
  IconEyeBase,
  IconEditBase,
  IconAudit,
  IconExportIconBlue,
  IconDeleteBlueSvg,
  IconReactivateSvg,
  IconCheckCircleIconGreenSvg,
  IconCrossCircleIconSvg,
} from "../../common/utils/icons";
import Loader from "../../components/Molecules/Loader/Loader";
import LimitChractersWithTooltip from "../../common/utils/LimitChractersWithTooltip";
import ButtonComponent from "../../components/Button.Component";
import AddSharpIcon from "@mui/icons-material/AddSharp";
import { GRID_DETAIL_PANEL_TOGGLE_COL_DEF } from "@mui/x-data-grid-pro";
import DropdownMenu from "../../components/Molecules/DropdownMenu/DropdownMenu";
import { useAuth } from "../../contexts/AuthContext";
import AssociateEntitiesDialog from "../../components/Dialogs/AssociateEntitiesDialog";
import SearchFilterBar from "../../components/Molecules/FilterSearch/SearchFilterBar";
import ConfirmDailog from "../../components/Dialogs/ConfirmDailog";
import {
  deleteComparisonResearchQuery,
  reactivateComparisonResearchQuery,
} from "../../services/comparisonResearchQueries";
import { getConfig } from "../../config/runtimeConfig";

// Centralized Filter Configuration for Comparison Research Queries
// Note: Field names match the internal state. The service layer (comparisonResearchQueries.tsx)
// handles transformation to API parameter names:
// - rule_id → rule_filter_ids (API parameter, supports comma-separated values)
// - comparison_research_query_id → comparison_research_queries_filter_ids (API parameter, supports comma-separated values)
// - status_filter → is_active (API parameter, boolean or omitted)
const COMPARISON_RESEARCH_QUERIES_FILTER_CONFIG = {
  // Default filter state - single source of truth
  DEFAULT_STATE: {
    rule_id: null, // Can be null, number, or array of numbers
    comparison_research_query_id: null, // Can be null, number, or array of numbers
    status_filter: "Active", // "Active" | "Inactive" | "All"
  },

  // Fields to persist in URL - extracted from default state keys
  get URL_FIELDS() {
    return Object.keys(this.DEFAULT_STATE);
  },

  // Field type mappings for URL serialization/deserialization
  FIELD_TYPES: {
    // Array fields that should be serialized as comma-separated strings in URL
    ARRAY_FIELDS: ["rule_id", "comparison_research_query_id"],
    // String fields
    STRING_FIELDS: ["status_filter"],
  },
} as const;

const ComparisonResearchQueries: React.FC = () => {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const location = useLocation();

  // Refs for tracking state to prevent circular updates
  const isUpdatingFromURL = useRef(false);
  const isInitialMount = useRef(true);

  // Function to serialize filter state to URL parameters
  const serializeFiltersToURL = useCallback((filterData: any) => {
    const params = new URLSearchParams();

    COMPARISON_RESEARCH_QUERIES_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const value = filterData[field];

      // Skip null, undefined, or empty string values
      if (value === null || value === undefined || value === "") {
        return;
      }

      // Handle array fields (rule_id, comparison_research_query_id)
      if (
        (
          COMPARISON_RESEARCH_QUERIES_FILTER_CONFIG.FIELD_TYPES
            .ARRAY_FIELDS as readonly string[]
        ).includes(field)
      ) {
        if (Array.isArray(value) && value.length > 0) {
          // Join array with commas for URL
          params.set(field, value.join(","));
        } else if (!Array.isArray(value)) {
          // Single value - convert to string
          params.set(field, String(value));
        }
      }
      // Handle string fields (status_filter)
      else if (
        (
          COMPARISON_RESEARCH_QUERIES_FILTER_CONFIG.FIELD_TYPES
            .STRING_FIELDS as readonly string[]
        ).includes(field)
      ) {
        params.set(field, String(value));
      }
    });

    return params;
  }, []);

  // Function to deserialize URL parameters to filter state
  const deserializeFiltersFromURL = useCallback(() => {
    const filterData: any = {
      ...COMPARISON_RESEARCH_QUERIES_FILTER_CONFIG.DEFAULT_STATE,
    };

    COMPARISON_RESEARCH_QUERIES_FILTER_CONFIG.URL_FIELDS.forEach((field) => {
      const urlValue = searchParams.get(field);

      if (urlValue === null || urlValue === "") {
        return;
      }

      // Handle array fields (rule_id, comparison_research_query_id)
      if (
        (
          COMPARISON_RESEARCH_QUERIES_FILTER_CONFIG.FIELD_TYPES
            .ARRAY_FIELDS as readonly string[]
        ).includes(field)
      ) {
        // Split comma-separated values and convert to numbers
        const values = urlValue
          .split(",")
          .map((v) => parseInt(v.trim(), 10))
          .filter((v) => !isNaN(v));

        if (values.length > 0) {
          filterData[field] = values;
        }
      }
      // Handle string fields (status_filter)
      else if (
        (
          COMPARISON_RESEARCH_QUERIES_FILTER_CONFIG.FIELD_TYPES
            .STRING_FIELDS as readonly string[]
        ).includes(field)
      ) {
        filterData[field] = urlValue;
      }
    });

    return filterData;
  }, [searchParams]);

  // Function to get initial filter state from URL or default
  const getInitialFilterState = useCallback(() => {
    // Check if there are any filter parameters in the URL
    const hasURLParams =
      COMPARISON_RESEARCH_QUERIES_FILTER_CONFIG.URL_FIELDS.some((field) =>
        searchParams.has(field),
      );

    if (hasURLParams) {
      // Initialize from URL parameters
      return deserializeFiltersFromURL();
    }

    // Use default state
    return { ...COMPARISON_RESEARCH_QUERIES_FILTER_CONFIG.DEFAULT_STATE };
  }, [searchParams, deserializeFiltersFromURL]);

  // Permission checks for comparison_research_query actions
  const canViewComparisonResearchQuery = hasPermission(
    "comparison_research_query",
    "view",
  );
  const canAuditComparison = hasPermission("comparison_research_query", "view");
  const canDeleteComparisonResearchQuery = hasPermission(
    "comparison_research_query",
    "delete",
  );
  const canEditComparisonResearchQuery = hasPermission(
    "comparison_research_query",
    "edit",
  );

  // states
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [fileData, setFileData] = useState<any>([]);

  // Pagination default values and states
  const defaultPage = getConfig("REACT_APP_DEFAULT_PAGE", "1");
  const { rowsPerPage } = useRowsPerPage();
  const [page, setPage] = useState<number>(
    defaultPage ? parseInt(defaultPage) : 1,
  );
  const [pSize, setPSize] = useState<number>(rowsPerPage);

  // SearchFilterBar state - initialize from URL or use defaults
  const [searchFilterData, setSearchFilterData] = useState<any>(
    getInitialFilterState(),
  );

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState<boolean>(false);
  const [queryToDelete, setQueryToDelete] = useState<any>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  // State for export functionality
  const [openAssociateEntitiesDialog, setOpenAssociateEntitiesDialog] =
    useState<boolean>(false);
  const [associateEntityId, setAssociateEntityId] = useState<number | null>(
    null,
  );
  const [isDownloadLoading, setIsDownloadLoading] = useState<boolean>(false);

  const handleAssociateEntitiesDialog = () => {
    setOpenAssociateEntitiesDialog(true);
  };

  // hooks
  const [fetchedComparisonResearchQueries, refetchComparisonResearchQueries] =
    useFetchPaginatedComparisonResearchQueries({
      setIsLoading,
      page,
      pSize,
      ruleId: searchFilterData?.rule_id ? String(searchFilterData.rule_id) : "",
      comparisonResearchQueryId: searchFilterData?.comparison_research_query_id
        ? String(searchFilterData.comparison_research_query_id)
        : "",
      statusFilter: searchFilterData?.status_filter || "",
    });

  useEffect(() => {
    if (fetchedComparisonResearchQueries?.items?.length > 0) {
      //filter the items whose rule_id is null
      const filteredData = fetchedComparisonResearchQueries.items.filter(
        (item: any) => item.rule_id !== null,
      );
      setFileData({ ...fetchedComparisonResearchQueries, items: filteredData });
    }
  }, [fetchedComparisonResearchQueries]);

  // URL Synchronization: Update URL when filter state changes (State → URL)
  useEffect(() => {
    // Skip URL update if we're currently updating from URL to prevent circular updates
    if (isUpdatingFromURL.current) {
      return;
    }

    // Skip on initial mount - URL is already set
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Serialize current filter state to URL parameters
    const params = serializeFiltersToURL(searchFilterData);

    // Update URL without adding to browser history (replace: true)
    setSearchParams(params, { replace: true });
  }, [searchFilterData, serializeFiltersToURL, setSearchParams]);

  // URL Synchronization: Update filter state when URL changes (URL → State)
  useEffect(() => {
    // Only update state from URL if the URL actually changed (e.g., browser back/forward)
    const currentURLParams = new URLSearchParams(location.search);
    const serializedCurrentState = serializeFiltersToURL(searchFilterData);

    // Compare URL params with current state
    const urlChanged =
      currentURLParams.toString() !== serializedCurrentState.toString();

    if (urlChanged && !isInitialMount.current) {
      // Set flag to prevent circular updates
      isUpdatingFromURL.current = true;

      // Deserialize URL parameters to filter state
      const newFilterState = deserializeFiltersFromURL();

      // Update filter state
      setSearchFilterData(newFilterState);

      // Reset flag after state update
      setTimeout(() => {
        isUpdatingFromURL.current = false;
      }, 0);
    }
  }, [
    location.search,
    deserializeFiltersFromURL,
    serializeFiltersToURL,
    searchFilterData,
  ]);

  // Reset page to 1 when search filter changes
  useEffect(() => {
    // Reset page when any filter changes (rule, comparison research query ID, or status)
    if (
      searchFilterData?.rule_id !== undefined ||
      searchFilterData?.comparison_research_query_id !== undefined ||
      searchFilterData?.status_filter !== undefined
    ) {
      setPage(1);
    }
  }, [searchFilterData]);

  // Handle delete comparison research query
  const handleDeleteQuery = (query: any) => {
    setQueryToDelete(query);
    setDeleteConfirmOpen(true);
  };

  // Confirm delete action
  const confirmDelete = async () => {
    if (!queryToDelete) return;

    try {
      setDeleteLoading(true);
      const result = await deleteComparisonResearchQuery(queryToDelete.id);

      if (result && result.status === 200) {
        showToast(
          `Comparison research query "${queryToDelete.code}" deleted successfully`,
          "success",
        );
        setDeleteConfirmOpen(false);
        setQueryToDelete(null);
        // Update the list to remove the deleted query
        setFileData((prev: any) => ({
          ...prev,
          items: prev?.items?.filter(
            (prevItem: any) => prevItem.id !== queryToDelete.id,
          ),
        }));
      }
    } catch (error: any) {
      showToast(
        error?.message || "Failed to delete comparison research query",
        "error",
      );
    } finally {
      setDeleteLoading(false);
      setDeleteConfirmOpen(false);
    }
  };
  const handleEdit = (id: number) =>
    navigate(`/comparison-research-queries/edit/${id}`);

  // Handle reactivate comparison research query
  const handleReactivate = async (id: number, code: string) => {
    setIsLoading(true);
    try {
      await reactivateComparisonResearchQuery(id);
      showToast(
        `Comparison Research Query "${code}" reactivated successfully`,
        "success",
      );
      // Refetch the list to update the UI
      refetchComparisonResearchQueries();
    } catch (error) {
      showToast("Error reactivating comparison research query", "error");
    } finally {
      setIsLoading(false);
    }
  };

  const columns: GridColDef[] = useMemo(
    () => [
      {
        ...GRID_DETAIL_PANEL_TOGGLE_COL_DEF,
        width: 1,
        minWidth: 20,
        flex: 0,
        renderCell: () => <></>,
        cellClassName: "no-padding-cell",
        headerClassName: "no-padding-cell",
      },
      {
        field: "comparison_research_query_tags_details",
        headerName: "Comparison research query id",
        width: 100,
        flex: 1,
        renderCell: (params: any) => {
          return <LimitChractersWithTooltip value={params?.id} />;
        },
      },
      {
        field: "code",
        headerName: "Comparison research query code",
        width: 100,
        flex: 1,
        renderCell: (params: any) => {
          return <LimitChractersWithTooltip value={params?.value} />;
        },
      },
      {
        field: "rule_id",
        headerName: "Rule ID",
        width: 100,
        flex: 1,
        renderCell: (params: any) => {
          return <LimitChractersWithTooltip value={params?.value || "N/A"} />;
        },
      },
      {
        field: "rule_code",
        headerName: "Rule code",
        width: 100,
        flex: 1,
        renderCell: (params: any) => {
          return <LimitChractersWithTooltip value={params?.value || "N/A"} />;
        },
      },
      {
        field: "modified_by_user",
        headerName: "Modified By",
        flex: 1,
        renderCell: (params: any) => {
          return <LimitChractersWithTooltip value={params?.value?.username} />;
        },
      },
      {
        field: "updated_on",
        headerName: "Last Modified",
        flex: 1,
        renderCell: (params: any) => {
          return (
            <span style={{ cursor: "pointer" }}>
              {getFormattedDateTime(params.value)}
              {/* {params.value} */}
            </span>
          );
        },
      },
      {
        field: "is_active",
        headerName: "Status",
        width: 100,
        align: "center",
        headerAlign: "center",
        renderCell: (params: any) => {
          return params.value ? (
            <Tooltip title="Active" placement="top" arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconCheckCircleIconGreenSvg width={21} height={21} />
              </Box>
            </Tooltip>
          ) : (
            <Tooltip title="Inactive" placement="top" arrow>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <IconCrossCircleIconSvg width={21} height={21} />
              </Box>
            </Tooltip>
          );
        },
      },
      {
        field: "actions",
        headerName: "Actions",
        flex: 0.5,
        minWidth: 100,
        sortable: false,
        renderCell: (params: any) => {
          const isActive = params.row.is_active;
          const handleDelete = () => {
            handleDeleteQuery(params.row);
          };
          // Create dropdown menu items based on permissions
          // Only show dropdown items for active records
          const dropdownItems = isActive
            ? [
                // Audit Comparison Query - requires view permission
                ...(canViewComparisonResearchQuery
                  ? [
                      <IconButton
                        key="audit-comparison-query"
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() =>
                          navigate(
                            `/comparison-research-queries/auditComparison/${params.row.id}`,
                          )
                        }
                      >
                        <IconAudit /> Audit
                      </IconButton>,
                    ]
                  : []),
                // Export Comparison Query - requires view permission
                ...(true
                  ? [
                      <IconButton
                        key="export"
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() => {
                          setAssociateEntityId(params.row.id);
                          handleAssociateEntitiesDialog();
                        }}
                      >
                        <IconExportIconBlue />
                        Export
                      </IconButton>,
                    ]
                  : []),
                // Delete Comparison Research Query - requires delete permission
                ...(canDeleteComparisonResearchQuery
                  ? [
                      <IconButton
                        key="delete-comparison-query"
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() => handleDelete()}
                      >
                        <IconDeleteBlueSvg />
                        Delete
                      </IconButton>,
                    ]
                  : []),
              ]
            : [];

          return (
            <>
              {/* For active records, show all action buttons */}
              {isActive && (
                <>
                  <Tooltip
                    title="View Comparison Research Query"
                    placement="top"
                    arrow
                  >
                    <IconButton
                      className="datagrid-action-btn"
                      color="inherit"
                      onClick={() => {
                        navigate(
                          `/comparison-research-queries/view/${params.row.rule_id}`,
                        );
                      }}
                    >
                      <IconEyeBase />
                    </IconButton>
                  </Tooltip>
                  {canEditComparisonResearchQuery && (
                    <Tooltip
                      title="Edit Comparison Research Query"
                      placement="top"
                      arrow
                    >
                      <IconButton
                        className="datagrid-action-btn"
                        color="inherit"
                        onClick={() => handleEdit(params.row.rule_id)}
                      >
                        <IconEditBase />
                      </IconButton>
                    </Tooltip>
                  )}
                  {dropdownItems.length > 0 && (
                    <DropdownMenu dropdownMenuItems={dropdownItems} />
                  )}
                </>
              )}

              {/* For inactive records, show ONLY Reactivate button */}
              {!isActive && canEditComparisonResearchQuery && (
                <Tooltip
                  title="Reactivate Comparison Research Query"
                  placement="top"
                  arrow
                >
                  <IconButton
                    className="datagrid-action-btn"
                    color="inherit"
                    onClick={() =>
                      handleReactivate(params.row.id, params.row.code)
                    }
                  >
                    <IconReactivateSvg />
                  </IconButton>
                </Tooltip>
              )}
            </>
          );
        },
      },
    ],
    [
      canAuditComparison,
      canDeleteComparisonResearchQuery,
      canEditComparisonResearchQuery,
      canViewComparisonResearchQuery,
      navigate,
      handleReactivate,
      handleEdit,
    ],
  );

  const enableRowGroupByColumnProps = {
    hideDescendantCount: true,
  };

  return (
    <Box className="div-container">
      {isLoading && <Loader isLoading={isLoading} />}
      {deleteLoading && <Loader isLoading={deleteLoading} zIndex={1400} />}
      <ConfirmDailog
        isDailogOpen={deleteConfirmOpen}
        handleCancelAction={() => setDeleteConfirmOpen(false)}
        handleConfirmAction={confirmDelete}
        dailogTitle="Delete Comparison Research Query"
        dailogDescription={`Are you sure you want to delete comparison research query "${queryToDelete?.code}"?`}
      />

      <Box className="text-box-card list-page-card compact-text-box-card">
        <Grid container rowSpacing={2.5} columnSpacing={2}>
          <Grid item xs>
            <SearchFilterBar
              largeWidth={true}
              setSearchFilterData={setSearchFilterData}
              searchFilterData={searchFilterData}
              requiredFields={[
                "rule_list",
                "comparison_research_query_id",
                "status_filter",
              ]}
              primaryFields={[
                "rule_list",
                "comparison_research_query_id",
                "status_filter",
              ]}
              fieldRequirements={{
                defaultStatusFilter: "Active", // Reset to Active status on Clear Filter
                includeComparisonResearchQueryId: true,
                hasMultipleRuleIds: true, // Enable multiple selection for rule_list filter
                hasMultipleComparisonResearchQueryIds: true, // Enable multiple selection for comparison_research_query_id filter
                includeRuleId: true,
              }}
            />
          </Grid>
          <Grid
            item
            xs="auto"
            sx={{ display: "flex", alignItems: "flex-start" }}
          >
            <ButtonComponent
              handelClickEvent={() =>
                navigate("/comparison-research-queries/manage", {
                  state: { isFromListPage: true },
                })
              }
              className="btn-orange"
            >
              <>
                <AddSharpIcon sx={{ marginRight: "4px" }} /> Comparison Research
                Query
              </>
            </ButtonComponent>
          </Grid>
        </Grid>
      </Box>

      <FlexBetween>
        <DataTable
          dataColumns={columns || []}
          dataRows={fileData?.items || []}
          loading={isLoading}
          dataListTitle={"Comparison Research Queries List"}
          className="dataTable no-radius hide-progress-icon"
          paginationMode="server"
          rowCount={fileData?.total || 0}
          rowGroupByColumnProps={enableRowGroupByColumnProps}
          paginationModel={{
            page: page - 1,
            pageSize: pSize,
          }}
          onPaginationModelChange={(params: any) => {
            if (params.pageSize !== pSize || params.page !== page - 1) {
              setPage(params.page + 1);
              setPSize(params.pageSize);
            }
          }}
          fitViewport
          singlePageMaxHeightDiff={338}
          hideExportButton={true}
        />
      </FlexBetween>
      {isDownloadLoading && <Loader isLoading />}
      <AssociateEntitiesDialog
        openAssociateEntitiesDialog={openAssociateEntitiesDialog}
        setOpenAssociateEntitiesDialog={setOpenAssociateEntitiesDialog}
        associateEntityId={associateEntityId}
        downloadExportFile={downloadComparisonResearchQueryExportFile}
        setIsDownloadLoading={setIsDownloadLoading}
        header="Comparison Research Query"
      />
    </Box>
  );
};

export default ComparisonResearchQueries;
